import { useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
  Pie,
  PieChart,
  Cell,
  Legend,
} from "recharts";
import { Card, Row, Col, Statistic, Button } from "antd";
import { LeftOutlined, RightOutlined, CalendarOutlined } from "@ant-design/icons";
import { IntelligenceDto } from "dtos/application.dto";
import { Payload } from "recharts/types/component/DefaultTooltipContent";
import { formatMoney } from "utils/functions";
import { twColors } from "utils/constants";

// Define types for chart data
type WeeklyProfitItem = {
  startDate: string;
  endDate: string;
  net_value: number;
  outcome: "profit" | "loss";
  percentage: number;
};

type WeeklyCreditItem = {
  day_name: string;
  average: number;
  percentage: number;
};

type OverViewProps = {
  data: IntelligenceDto;
};

const COLORS = ["#3b82f6", "#22c55e", "#f97316", "#ec4899", "#8b5cf6", "#06b6d4", "#eab308"];

const WeeklyAnalytics = ({ data }: OverViewProps) => {
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 20;

  const netWeeklyProfit = data.net_weekly_profit ?? [];
  const weeklyCreditDist = data.weekly_credit_distribution ?? [];

  const totalPages = Math.ceil(netWeeklyProfit.length / itemsPerPage);
  const currentData = netWeeklyProfit.slice(currentPage * itemsPerPage, (currentPage + 1) * itemsPerPage);

  return (
    <div className="flex flex-col gap-6">
      {/* Summary Cards */}
      <Row gutter={16}>
        <Col span={6}>
          <Card className="border-1 border-gray-300 ">
            <Statistic
              title="Weekly Profit Std"
              value={data.net_weekly_profit_std}
              valueStyle={{
                color: Number(data?.net_weekly_profit_std ?? 0) > 0 ? twColors.green600 : twColors.red600,
              }}
              formatter={(value) => `${formatMoney(Number(value), false, false)}`}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card className="border-1 border-gray-300 ">
            <Statistic
              title="Revenue Std"
              value={data.net_weekly_revenue_std}
              valueStyle={{
                color: Number(data?.net_weekly_revenue_std ?? 0) > 0 ? twColors.green600 : twColors.red600,
              }}
              formatter={(value) => `${formatMoney(Number(value), false, false)}`}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card className="border-1 border-gray-300 ">
            <Statistic
              title="Profit Variance"
              value={data.net_weekly_profit_variance}
              valueStyle={{
                color: Number(data?.net_weekly_profit_variance ?? 0) > 0 ? twColors.green600 : twColors.red600,
              }}
              formatter={(value) => `${formatMoney(Number(value), false, false)}`}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card className="border-1 border-gray-300 ">
            <Statistic
              title="Revenue Variance"
              value={data.net_weekly_revenue_variance}
              valueStyle={{
                color: Number(data?.net_weekly_revenue_variance ?? 0) > 0 ? twColors.green600 : twColors.red600,
              }}
              formatter={(value) => `${formatMoney(Number(value), false, false)}`}
            />
          </Card>
        </Col>
      </Row>

      {/* Weekly Credit Distribution */}
      <Row gutter={16}>
        <Col span={12}>
          <Card className="border-1 border-gray-300 " title="Weekly Credit Distribution by Day">
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={weeklyCreditDist}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    dataKey="average"
                    label={(props) => {
                      const p = (props as any)?.payload;
                      return `${p?.day_name ?? ""}: ${p?.percentage ?? 0}%`;
                    }}
                  >
                    {weeklyCreditDist.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value: number) => [`$${Number(value).toLocaleString()}`, "Average"]} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </Col>
        <Col span={12}>
          <Card className="border-1 border-gray-300 " title="Daily Average Distribution">
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={weeklyCreditDist}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="day_name" tick={{ fill: "#374151", fontSize: 10 }} />
                  <YAxis tick={{ fill: "#374151", fontSize: 12 }} />
                  <Tooltip formatter={(value: number) => [`$${Number(value).toLocaleString()}`, "Average"]} />
                  <Legend />
                  <Bar dataKey="average" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Weekly Profit Trend */}
      <Card
        title="Weekly Profit Trend"
        className="border-1 border-gray-300 "
        extra={
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <span>
              Page {currentPage + 1} of {totalPages}
            </span>
            <Button
              icon={<LeftOutlined />}
              size="small"
              onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
              disabled={currentPage === 0}
            />
            <Button
              icon={<RightOutlined />}
              size="small"
              onClick={() => setCurrentPage(Math.min(totalPages - 1, currentPage + 1))}
              disabled={currentPage === totalPages - 1}
            />
          </div>
        }
      >
        <div className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={currentData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey={(item: WeeklyProfitItem) =>
                  new Date(item.startDate).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                  })
                }
                tick={{ fill: "#374151", fontSize: 10 }}
                angle={-45}
                textAnchor="end"
                height={60}
              />
              <YAxis tick={{ fill: "#374151", fontSize: 12 }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: "white",
                  border: "1px solid #d1d5db",
                  borderRadius: "6px",
                }}
                formatter={(value: number, name: string, item: Payload<number, string>) => {
                  const p = item?.payload as WeeklyProfitItem | undefined;
                  const outcome = p?.outcome === "profit" ? "Profit" : "Loss";
                  const percentage = p?.percentage?.toFixed(1) ?? "0.0";
                  return [`$${Number(value).toLocaleString()}`, `${outcome} (${percentage}%)`];
                }}
              />
              <Bar dataKey="net_value" radius={[2, 2, 0, 0]}>
                {currentData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.outcome === "profit" ? "#22c55e" : "#ef4444"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>
    </div>
  );
};

export default WeeklyAnalytics;
